import {
  Box,
  Button,
  Card,
  Grid2,
  Typography,
  IconButton,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import SignInDialog from '../../components/common/SignInDialog';
import {
  CustomField,
  customFieldTypeLabels,
} from '../../components/common/types';
import api from '../../redux/features/auth/axiosAuthHelper';
import { CardContent, Modal } from '@mui/material';
import CustomFieldCard from '../../components/common/cards/CustomFieldCard';
import DeleteIcon from '@mui/icons-material/Delete';

type Props = {};

const CustomFieldsPage = (props: Props) => {
  const authData = useSelector((state: RootState) => state.auth);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [selectedField, setSelectedField] = useState<CustomField | undefined>();
  const [openModal, setOpenModal] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [hoveredField, setHoveredField] = useState<number | undefined>();
  const maxFields = 4;

  useEffect(() => {
    const fetchFields = async () => {
      try {
        const response = await api.get('dynamic-fields', {
          headers: {
            Authorization: `Bearer ${authData.access}`,
          },
        });
        setShowAdd(response.data['count'] < maxFields);
        setCustomFields(response.data['results'] as CustomField[]);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
      }
    };
    fetchFields();
  }, [refresh]);

  const handleCardClick = (field: CustomField) => {
    setSelectedField(field);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedField(undefined);
  };

  const handleRefresh = () => {
    setOpenModal(false);
    setSelectedField(undefined);
    setRefresh(!refresh);
  };

  const handleDeleteField = async (fieldId: number) => {
    try {
      await api.delete(`dynamic-fields/${fieldId}/`, {
        headers: { Authorization: `Bearer ${authData.access}` },
      });
      setRefresh(!refresh);
    } catch (error) {
      console.error('Error deleting custom field:', error);
    }
  };

  return authData.isAuthenticated === false ? (
    <SignInDialog isAuthenticated={authData.isAuthenticated} />
  ) : authData.isAuthenticated && authData.isOrganisationAdmin ? (
    <Box p={10} paddingX={20} alignItems="center">
      <Typography
        data-testid="Typography-usiu"
        variant="h6"
        sx={{
          textAlign: 'center',
          minWidth: '100%',
          pt: 1,
          paddingLeft: 1.5,
          paddingRight: 3,
          margin: 'auto',
          borderRadius: 1,
        }}
      >
        Custom Fields
      </Typography>

      {showAdd && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setSelectedField(undefined);
            setOpenModal(true);
          }}
        >
          Add Custom Field
        </Button>
      )}

      <Grid2 container spacing={3} mt={2}>
        {customFields.map((field) => (
          <Grid2
            sx={{
              width: { xs: '100%', sm: '50%', md: '31%' },
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
            key={field.id}
            onMouseEnter={() => setHoveredField(field.id)}
            onMouseLeave={() => setHoveredField(undefined)}
          >
            <Card
              onClick={() => handleCardClick(field)}
              sx={{ cursor: 'pointer' }}
            >
              <CardContent>
                <Typography variant="h5">
                  {field.type !== 'BARCODE' ? field.title : 'Barcode'}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Type: {customFieldTypeLabels[field.type]}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  {field.type !== 'BARCODE'
                    ? `Key: ${field.key}`
                    : `Regex: ${field.regex}`}
                </Typography>
              </CardContent>
            </Card>

            {hoveredField === field.id && (
              <IconButton
                sx={{
                  position: 'absolute',
                  top: 5,
                  right: 5,
                  backgroundColor: 'rgba(255, 0, 0, 0.8)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 0, 0, 1)',
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation(); // Prevents triggering card click
                  handleDeleteField(field.id);
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Grid2>
        ))}
      </Grid2>

      <CustomFieldModal
        open={openModal}
        field={selectedField}
        onClose={handleCloseModal}
        onSave={handleRefresh}
      />
    </Box>
  ) : null;
};

const CustomFieldModal: React.FC<{
  open: boolean;
  field?: CustomField;
  onClose: () => void;
  onSave: () => void;
}> = ({ open, field, onClose, onSave }) => {
  return (
    <Modal open={open} onClose={onClose}>
      <CustomFieldCard
        customFieldId={field?.id}
        onBack={onClose}
        onSave={onSave}
      />
    </Modal>
  );
};

export default CustomFieldsPage;
